import { cloneElement, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Bar as RBar, BarChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { api, errorMessage } from "@/lib/api";
import { DataTable, Bar, num } from "@/lib/admin";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";

const Kpi = ({ label, value, sub, testId }) => (
    <div className="edi-card p-4 border-t-4 border-t-edi-lime" data-testid={testId}>
        <p className="text-sm font-medium text-edi-muted">{label}</p>
        <p className="font-heading text-3xl font-black mt-1" data-testid={`${testId}-value`}>{value}</p>
        {sub && <p className="text-sm text-edi-muted">{sub}</p>}
    </div>
);

const dayLabel = (d) => { const [, m, dd] = d.split("-"); return `${dd}/${m}`; };
const PctCell = ({ value }) => <span className="flex items-center gap-2"><Bar value={value} />{num(value)}%</span>;

const ChartBox = ({ title, id, testId, label, children, table }) => (
    <section className="edi-card p-4" aria-labelledby={id} data-testid={testId}>
        <h2 id={id} className="font-heading font-bold text-base">{title}</h2>
        <div className="h-56 mt-2 text-edi-text" role="img" aria-label={label}>
            {cloneElement(children, { responsive: true, style: { width: "100%", height: "100%" } })}
        </div>
        <details className="mt-2" data-testid={`${testId}-table-toggle`}>
            <summary className="cursor-pointer underline font-medium">Mostra i dati in tabella</summary>
            <div className="mt-2">{table}</div>
        </details>
    </section>
);

const useSorted = (rows, initial) => {
    const [sort, setSort] = useState(initial);
    const onSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
    const sorted = [...rows].sort((a, b) => {
        const va = a[sort.key], vb = b[sort.key];
        const c = typeof va === "string" ? va.localeCompare(vb, "it") : va - vb;
        return sort.dir === "asc" ? c : -c;
    });
    return { sorted, sort, onSort };
};

const SediTable = ({ rows }) => {
    const [params, setParams] = useSearchParams();
    const { sorted, sort, onSort } = useSorted(rows, { key: "accessi", dir: "desc" });
    const pick = (r) => { const n = new URLSearchParams(params); n.delete("spin"); n.delete("regione"); n.append("spin", r.key); setParams(n); };
    return (
        <DataTable caption="Attività per sede SPIN (seleziona una sede per filtrare il pannello)" testId="an-sedi" rows={sorted} sort={sort} onSort={onSort} onRowClick={pick} columns={[
            { key: "label", label: "Sede SPIN" }, { key: "accessi", label: "Accessi" }, { key: "aziende_attive", label: "Aziende attive" },
            { key: "registrazioni", label: "Nuove registrazioni" }, { key: "assessment", label: "Assessment completati" }, { key: "chat", label: "Aperture chat AI" },
        ]} />
    );
};

const Charts = ({ d }) => (
    <>
        <ChartBox title="Accessi e utenti attivi per giorno" id="an-daily-title" testId="an-daily" label="Grafico giornaliero di accessi e utenti attivi: i dati sono nella tabella collegata"
            table={<DataTable caption="Accessi e utenti attivi per giorno" testId="an-daily-table" rows={d.daily} columns={[{ key: "date", label: "Giorno", render: (r) => dayLabel(r.date) }, { key: "logins", label: "Accessi" }, { key: "utenti", label: "Utenti attivi" }]} />}>
            <LineChart data={d.daily.map((r) => ({ ...r, g: dayLabel(r.date) }))} margin={{ left: -16, right: 12, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#9aa2a5" />
                <XAxis dataKey="g" tick={{ fill: "currentColor" }} minTickGap={16} /><YAxis allowDecimals={false} tick={{ fill: "currentColor" }} /><Tooltip /><Legend formatter={(v) => <span className="text-edi-text">{v}</span>} />
                <Line dataKey="logins" name="Accessi" stroke="currentColor" strokeWidth={2} dot={{ r: 4, fill: "#BCCF00", stroke: "currentColor", strokeWidth: 1.5 }} isAnimationActive={false} />
                <Line dataKey="utenti" name="Utenti attivi (tratteggiata)" stroke="currentColor" strokeWidth={2} strokeDasharray="6 3" dot={false} isAnimationActive={false} />
            </LineChart>
        </ChartBox>
        <ChartBox title="Accessi per ora del giorno (ora italiana)" id="an-hours-title" testId="an-hours" label="Grafico degli accessi per ora del giorno: i dati sono nella tabella collegata"
            table={<DataTable caption="Accessi per ora del giorno" testId="an-hours-table" rows={d.hours} columns={[{ key: "hour", label: "Ora", render: (r) => `${String(r.hour).padStart(2, "0")}:00` }, { key: "logins", label: "Accessi" }]} />}>
            <BarChart data={d.hours} margin={{ left: -16, right: 12, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#9aa2a5" />
                <XAxis dataKey="hour" tick={{ fill: "currentColor" }} /><YAxis allowDecimals={false} tick={{ fill: "currentColor" }} /><Tooltip />
                <RBar dataKey="logins" name="Accessi" fill="#BCCF00" stroke="currentColor" strokeWidth={1} isAnimationActive={false} />
            </BarChart>
        </ChartBox>
    </>
);

const Panel = ({ d, isSuper }) => {
    const k = d.kpi;
    return (
        <div className="space-y-6" data-testid="analytics-panel">
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                <Kpi label="Accessi" value={k.accessi} testId="an-kpi-accessi" />
                <Kpi label="Utenti attivi" value={k.utenti_attivi} testId="an-kpi-utenti" />
                <Kpi label="Sessioni" value={k.sessioni} testId="an-kpi-sessioni" />
                <Kpi label="Durata media sessione" value={k.durata_media} sub="minuti:secondi" testId="an-kpi-durata" />
                <Kpi label="Pagine viste" value={k.pagine_viste} testId="an-kpi-pagine" />
                <Kpi label="Nuove registrazioni" value={k.registrazioni} testId="an-kpi-registrazioni" />
                <Kpi label="Assessment completati" value={k.assessment_completati} testId="an-kpi-assessment" />
                <Kpi label="Aperture chat AI" value={k.chat_aperte} testId="an-kpi-chat" />
                <Kpi label="Clic chat bloccati per consenso" value={k.chat_bloccate} sub={`${num(k.chat_bloccate_pct)}% dei clic sul pulsante chat`} testId="an-kpi-bloccati" />
                <Kpi label="Accessi falliti" value={k.accessi_falliti} testId="an-kpi-falliti" />
            </div>
            {d.empty ? (
                <Notice kind="info" testId="analytics-empty">Nessun dato disponibile per il periodo selezionato</Notice>
            ) : (
                <>
                    <Charts d={d} />
                    <DataTable caption="Pagine più viste (prime 10)" testId="an-pages" rows={d.pages} columns={[{ key: "route", label: "Pagina", render: (r) => <span className="break-all">{r.route}</span> }, { key: "views", label: "Visualizzazioni" }, { key: "utenti", label: "Utenti distinti" }]} />
                    <div className="grid gap-6 xl:grid-cols-2">
                        <DataTable caption="Sessioni per dispositivo" testId="an-devices" rows={d.devices} columns={[{ key: "label", label: "Dispositivo" }, { key: "sessioni", label: "Sessioni" }, { key: "pct", label: "%", render: (r) => <PctCell value={r.pct} /> }]} />
                        <DataTable caption="Sessioni per browser" testId="an-browsers" rows={d.browsers} columns={[{ key: "label", label: "Browser" }, { key: "sessioni", label: "Sessioni" }, { key: "pct", label: "%", render: (r) => <PctCell value={r.pct} /> }]} />
                    </div>
                    {isSuper && <DataTable caption="Accessi per ruolo" testId="an-roles" rows={d.roles} columns={[{ key: "label", label: "Ruolo" }, { key: "logins", label: "Accessi" }, { key: "pct", label: "%", render: (r) => <PctCell value={r.pct} /> }]} />}
                </>
            )}
            {isSuper && <SediTable rows={d.sedi} />}
        </div>
    );
};

export const AnalyticsPanel = ({ isSuper }) => {
    const { search } = useLocation();
    const [d, setD] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        const qs = new URLSearchParams(search);
        ["vista", "page", "tipo", "q"].forEach((x) => qs.delete(x));
        if (qs.get("periodo") === "custom" && !qs.get("from")) qs.delete("periodo");
        setError("");
        api.get(`/admin/analytics?${qs}`).then(({ data }) => setD(data)).catch((e) => setError(errorMessage(e)));
    }, [search]);
    if (error) return <Notice kind="error" role="alert" testId="analytics-error">{error}</Notice>;
    if (!d) return <Loading />;
    return <Panel d={d} isSuper={isSuper} />;
};
